/**
 * Shared error filtering constants used by PostHog error tracking
 * to ensure consistent exception suppression.
 */

/** Error messages to ignore — matches against exception message text */
export const IGNORED_ERROR_MESSAGES = [
  "Maximum number of retries reached while waiting for window.ga to appear (already waited for 5500ms). window.gtag is available but it does not appear to load window.ga anymore!",
  "Failed to fetch RSC payload. Falling back to browser navigation. TypeError: Load failed",
  "Load failed",
  "TypeError: Failed to fetch",
  "TypeError: NetworkError when attempting to fetch resource",
  "UnhandledRejection: Non-Error promise rejection captured with value: undefined",
  "UET is not defined",
  "Error: Extension context invalidated.",
  "ResizeObserver loop limit exceeded",
  "Can't find variable: globalThis",
  "ResizeObserver loop completed with undelivered notifications",
  "The operation would yield an incorrect node tree.",
  // React 19's hoistable-resource commit (acquireResource →
  // document.head.appendChild) throws when `document.head` is null, which only
  // happens in degraded DOM environments like in-app browsers/webviews or
  // extensions that strip the <head>. The stack is 100% minified react-dom
  // internals with no site-code frames, so there is nothing to fix in our code.
  // Blink/Chrome and WebKit/Safari word the same crash differently, so suppress
  // both phrasings.
  "Failed to execute 'appendChild' on 'Node': Only one element on document allowed.",
  "null is not an object (evaluating 'e.head.appendChild')",
  "Cannot read properties of undefined (reading 'call')",
  "undefined is not an object (evaluating 's[e].call')",
  "Maximum update depth exceeded. This can happen when a component repeatedly calls setState inside componentWillUpdate or componentDidUpdate. React limits the number of nested updates to prevent infinite loops.",
  "Non-Error exception captured",
  "Non-Error promise rejection captured",
  "Minified React error #422; visit https://react.dev/errors/422 for the full message or use the non-minified dev environment for full errors and additional helpful warnings.",
  "undefined is not an object (evaluating 'l[e].call')",
  "Unknown root exit status",
  "Unknown root exit status.",
  "Cannot read properties of null (reading 'setItem')",
  "TypeError: Cannot read properties of null (reading 'setItem')",
  "ReferenceError: event_type is not defined",
  "ReferenceError: Can't find variable: event_type",
  "Can't find variable: event_type",
  "Cannot read properties of null (reading 'ref')",
  "null is not an object (evaluating 'e.ref')",
  "Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone",
  "Can't find variable: axon",
  // A browser extension / ad-network in-app-browser WebView injects a script
  // into the page's global scope that references an undefined `_G`. The only
  // stack frame is `global code` at line 1 of the page-document URL itself (not
  // a `/_next/static/` chunk), and there is no `_G` variable anywhere in the
  // repo — so this is never site code. Because the frame is the page URL, one
  // crawler/extension session fans out into a fresh issue per landing page.
  // Matched as a substring (both the bare and ReferenceError-prefixed forms,
  // mirroring the `event_type` precedent above) so every per-page fingerprint
  // collapses into one.
  "ReferenceError: Can't find variable: _G",
  "Can't find variable: _G",
  // An autofill / form-filler browser extension injects a script that runs in
  // page context (`execute_auto_fill` at line 1 of the page-document URL) and
  // references an undefined `xbrowser`. Neither `xbrowser` nor
  // `execute_auto_fill` exists anywhere in the repo, so this is never site code
  // — the instrumented wrapper frame above it just catches the throw. Because
  // the only meaningful frame is the page URL, one extension session fans out
  // into a fresh issue per landing page.
  // Matched as a substring (both the bare and ReferenceError-prefixed forms,
  // mirroring the `_G`/`event_type` precedent above) so every per-page
  // fingerprint collapses into one.
  "ReferenceError: xbrowser is not defined",
  "xbrowser is not defined",
  // A browser extension / ad-tech tag / stale GTM Custom HTML tag injects a
  // script referencing the undefined `OneTrust` global (a cookie-consent CMP)
  // before its loader is present. The only stack frame is `global code` at line
  // 1 of the page-document URL itself, and `OneTrust` appears nowhere in the
  // repo — our consent platform is Cookie Script — so this is never site code.
  // Because the frame is the page URL, one crawler/extension session fans out
  // into a fresh issue per landing page. Matched as a substring (WebKit's bare
  // and ReferenceError-prefixed forms plus Blink's "is not defined" phrasing,
  // mirroring the `_G` precedent above) so every per-page fingerprint collapses
  // into one in PostHog.
  "ReferenceError: Can't find variable: OneTrust",
  "Can't find variable: OneTrust",
  "OneTrust is not defined",
  "AbortError: The play() request was interrupted because video-only background media was paused to save power. https://goo.gl/LdLk22",
  "Error: AbortError: The operation was aborted.",
  "AbortError: The operation was aborted.",
  // Crypto-wallet browser extensions (MetaMask, Coinbase Wallet, Phantom, etc.)
  // inject `window.ethereum` into the page; their content scripts throw this
  // from `global code` and spawn a new error-tracking issue per page. Not site
  // code — matched as a substring so it collapses every per-page fingerprint.
  "window.ethereum",
  // Crypto-wallet browser extensions inject content scripts that reference
  // BIP-44 `CoinType`; when their injected object is undefined they throw
  // `Cannot read properties of undefined (reading 'CoinType')` with no stack
  // (synthetic, unhandled) from page/global context. Not site code — the repo
  // has zero `CoinType` references. Matched as a substring so every per-URL
  // fingerprint collapses into one and it drops across browser phrasings.
  "CoinType",
  // The Dark Reader browser extension injects a content script that runs in
  // page context and throws this from `global code` (line 1 of the HTML
  // document itself). Not site code — and because the stack frame is the page
  // URL, it fans one user's session out into a fresh issue per page. Matched as
  // a substring so every per-URL fingerprint collapses into one.
  "Can't find variable: DarkReader",
  // Firefox for iOS injects its own content scripts (`window.__firefox__.reader`,
  // `window.__firefox__.refresh_youtube_quality_*`) that throw this from `global
  // code` at line 1 of the page document. Not site code — no `__firefox__`
  // reference exists in the repo — and because the stack frame is the page URL,
  // it fans one user's session out into a fresh issue per page. Matched as a
  // substring so every per-URL fingerprint collapses into one.
  "window.__firefox__",
  // Injected anti-detect/stealth scripts (and some extensions) redefine
  // navigator.userAgent via Object.defineProperty. Baerskin code never does
  // this — it only reads userAgent — so the error is always bot/extension
  // noise. The page-document stack frame changes per URL, fanning one crawler
  // session out into many distinct issues, so suppress by message.
  'can\'t redefine non-configurable property "userAgent"',
  // PostHog's session-replay recorder (rrweb, bundled as posthog-recorder.js)
  // walks the DOM and tries to read into cross-origin iframes — the
  // youtube-nocookie embed in RemoteVideo, Stripe/PayPal payment frames, etc.
  // Firefox blocks that and throws `Permission denied to access property
  // "<prop>"` (commonly "nodeType"), which the recorder catches but posthog-js
  // still captures as a $exception. It is 100% Firefox noise — the recorder
  // handles its own failure, so the shopping flow is unaffected. Firefox emits
  // it both as a plain `Error` and (rarely) as a `DOMException` named
  // SecurityError; matching the message as a substring drops both variants and
  // collapses the per-property fingerprints into nothing.
  "Permission denied to access property",
  // A browser extension's content-script messaging layer (chrome.runtime /
  // browser.runtime port plumbing) throws this when it tries to post to a
  // disconnected port — seen on Mobile Safari / iOS via Facebook in-app
  // traffic. It is a wrapped non-Error with no in-app stack frames; the repo
  // has zero `chrome.runtime` / `onConnect` / port references, so it is never
  // site code and the shopping flow is unaffected. The port name embeds a
  // per-session UUID (`Port 'content <uuid>' not found. ports:`), so every
  // occurrence fingerprints into a brand-new issue. Matched on the UUID-free
  // substring so all of them collapse into nothing.
  "not found. ports:",
  // Vercel's Data Cache client (`K.set` in `/opt/rust/next-data.js`, injected
  // into the lambda runtime — not app code, not next/dist) throws
  // `invalid response from cache for ["dpl_...",...]: <status>` as an
  // unhandled rejection when its cache-write POST to
  // *.suspense-cache.vercel-infra.com gets a 502/503/408. The status is the
  // cache backend's, NOT the upstream service's — the upstream URL in the
  // message is just part of the "use cache" key, and the write only happens
  // after the upstream fetch already succeeded. Zero user impact: the render
  // already has the data, and `packages/offer/src/fetchers/base.ts` falls back
  // to a direct fetch if the cache wrapper fails. The throw happens inside
  // Vercel's runtime after the wrapped function returns, so no app-level
  // try/catch can reach it — client-side filtering is the only suppression
  // point. The bracketed-JSON-key prefix cannot collide with any app-authored
  // message. 783k events / 0 users since 2026-03-19 (STORE-3N8 / DEV-9738).
  'invalid response from cache for ["',
  // The browser's opaque cross-origin error: any exception thrown by a
  // third-party script served without CORS headers is masked to the literal
  // "Script error." with no stack, no frames, nothing actionable. 402k
  // occurrences / 128k users in 14 days — by far the biggest noise source.
  // Site-code errors are never masked (same-origin + crossorigin bundles).
  "Script error.",
  // The Goaffpro affiliate loader (a GTM tag, not repo code) throws this
  // self-described informational message when GTM fires it twice. Its own
  // message says to ignore it (STORE-9).
  "Goaffpro is already loaded. This message prevents duplicate loading of the script.",
  // Android WebView JS<->native bridge teardown: when the hosting app
  // destroys the WebView mid-call, every pending bridge invocation rejects
  // with "Error invoking <method>: Java object is gone". The method name
  // varies (postMessage, enableButtonsClickedMetaDataLogging, ...), fanning
  // one WebView teardown into many issues — match the shared suffix. Never
  // site code; supersedes the single enableDidUserTypeOnKeyboardLogging
  // entry above (kept for history) (STORE-4F3).
  "Java object is gone",
  // Sibling of "Java object is gone": the same Android WebView JS<->native
  // bridge also rejects pending calls with "Error invoking <method>: Java
  // bridge method invocation error" (seen on onGetContentHeight, postMessage,
  // ...). The frame it points at (report_content_height) is injected in-app
  // browser script, not repo code. The method name varies, so each variant
  // fans out into its own issue — match the shared suffix (STORE-4F3).
  "Java bridge method invocation error",
  // iOS in-app browsers (Facebook/Instagram/TikTok WKWebView) inject bridge
  // scripts that reference window.webkit.messageHandlers; when the handler
  // set is torn down they throw from page context. The repo has zero
  // window.webkit references — never site code (STORE-4F5).
  "window.webkit.messageHandlers",
  // A WKWebView bridge timeout thrown by the hosting app's injected script
  // when the native side stops responding (backgrounded webview). Not site
  // code — no postMessage bridge exists in the repo.
  "WKWebView API client did not respond to this postMessage",
  // A GTM custom-HTML tag calls window.getScrollDepth() before the tag that
  // defines it has loaded (tag-sequencing race). The repo has zero
  // getScrollDepth references — fix belongs in the GTM container; the error
  // is unactionable from site code (STORE-4R4).
  "window.getScrollDepth is not a function",
  // Google Maps JS API failed to fetch its "places" sub-module — network
  // flake or content blocker on the client. The address-autocomplete
  // gracefully degrades to manual entry; Google's loader owns the failure
  // (STORE-3T).
  'Could not load "places".',
  // @stripe/stripe-js loader rejection when the client cannot fetch
  // https://js.stripe.com — ad-blockers and privacy proxies. The loader
  // already retries; checkout surfaces its own user-facing error state.
  // Payment-flow failures report through the payment errorHandler, not this
  // generic load error (STORE-EP / STORE-4P1).
  "Failed to load Stripe.js",
  // Browser-extension content-script messaging that lost its tab/port —
  // same chrome.runtime plumbing class as the "not found. ports:" entry
  // above (STORE-9T).
  "Invalid call to runtime.sendMessage(). Tab not found.",
  // Extension content-script visibility polling that timed out against its
  // own background page (message embeds a per-session hash, fanning into
  // fresh issues). No contentScript references exist in the repo.
  "contentScriptVisibilityChanged",
  // Safari throws this DOMException when Stripe's stripe.js probes
  // ApplePaySession from an insecure/embedded document — sandboxed iframes
  // and in-app webviews. Apple Pay is simply unavailable there; the payment
  // sheet offers other methods.
  "Trying to start an Apple Pay session from an insecure document",
  // Same Stripe insecure-context noise as the entry above, different phrasing:
  // stripe.js throws this IntegrationError when the ExpressCheckoutElement
  // probes for Apple Pay from a non-HTTPS or embedded document (in-app
  // webviews, sandboxed iframes, an insecure parent frame). Apple Pay is simply
  // unavailable there; the payment sheet offers other methods. The stack frame
  // is the page URL, so one session otherwise fans out into a fresh issue per
  // page — match the substring so every fingerprint collapses into one.
  "Apple Pay requires your page to be served over HTTPS",
  // Injected third-party scripts (Google Translate's DOM rewriting, reader
  // modes, extensions) walk detached nodes and throw this from `global
  // code` at line 1 of the page-document URL itself — never a /_next/
  // chunk. Server code has no DOM, so the substring cannot mask a real
  // node-side failure; the page-URL frame fans one session into a fresh
  // issue per landing page (STORE-3MR / STORE-41E / STORE-359).
  "Cannot read properties of null (reading 'parentNode')",
  // Google Maps JS API failed to fetch its "map" sub-module — same
  // loader/network class as the "places" entry above.
  'Could not load "map".',
  // Media autoplay churn: navigating away or swapping the video source
  // interrupts a pending play() promise. Sibling of the power-save AbortError
  // entry above; browser behavior, not site code (STORE-6Z).
  "AbortError: The play() request was interrupted by a new load request",
  // A GTM custom-HTML tag calls window.getElementCount() before its defining
  // tag loads — same tag-sequencing race as getScrollDepth above; zero
  // getElementCount references in the repo (STORE-KB).
  "window.getElementCount is not a function",
  // A minified third-party script calls Element.prototype.checkVisibility(), a
  // DOM API that only shipped in Safari 17.4 — so on older WebKit (e.g. Safari
  // 16.6 / macOS Catalina) it is undefined and throws. `checkVisibility` exists
  // nowhere in the repo, so this is never site code; the fix belongs in the
  // third-party bundle. The receiver is minified (`a.checkVisibility …`) and the
  // stack frame is the page-document URL, so one old-browser session fans out
  // into a fresh issue per landing page. Matched on the receiver-agnostic
  // substring so every per-variable/per-page fingerprint collapses into one.
  "checkVisibility is not a function",
  // In-app webview (Instagram/Facebook iOS) performance-logger bridge —
  // injected script references its own navigationPerformanceLoggerWithReply
  // handler after the native side detaches. Not site code (STORE-4FS).
  "navigationPerformanceLoggerWithReply",
  // Stale-deploy chunk-hash misses: a client on an old HTML shell requests a
  // chunk that no longer exists after a deploy. Transient — navigation/reload
  // self-heals — and there is no chunk-retry machinery in the repo to fix.
  "ChunkLoadError",
  // Same deploy-skew class, server-side: a stale client POSTs a Server
  // Action ID the new deployment no longer knows. Next's own message; only
  // fires from stale clients (STORE-4E7).
  "Failed to find Server Action",
  // Browser-extension tab-messaging listener torn down mid-flight — same
  // chrome.runtime plumbing class as the entries above.
  "No Listener: tabs:outgoing.message.ready",
  // The Bloomreach Engagement / Exponea webxp SDK (injected into the checkout
  // page; the URL carries `xnpe_tifc` / `utm_source=bloomreach`) reads
  // `resp.payload` in its minified bundle and throws when `resp` is undefined.
  // Safari surfaces this as `undefined is not an object (evaluating
  // 'resp.payload')`. The exception is synthetic with ZERO stack frames, so the
  // URL/trace-based Bloomreach filters (DENIED_URL_PATTERNS,
  // BLOOMREACH_TRACE_PATTERNS) have nothing to match on — a message entry is the
  // only suppression point that works. No `resp.payload` read exists in our
  // storefront source, and the WebKit "undefined is not an object (evaluating
  // …)" phrasing is browser-only (Node/V8 uses the "Cannot read properties…"
  // form), so this can never collide with any server-side error handling that
  // also consumes this list.
  //
  // The Chrome/V8 counterpart, `Cannot read properties of undefined (reading
  // 'payload')`, is deliberately NOT added: `payload` is a generic property
  // name (unlike the unique `CoinType` / `window.ethereum` tokens above), and as
  // a bare substring in this shared list it would mask genuine server-side
  // `.payload` reads — the same server-side collision hazard documented for
  // `Connection closed.` below.
  "undefined is not an object (evaluating 'resp.payload')",
  // A minified third-party social-proof "activity bubble" widget injected on
  // the storefront reads/writes `this.activityData.bubble_shown_today` in its
  // bundle and throws when `activityData` is null. Safari surfaces this as
  // `null is not an object (evaluating
  // 'this.activityData.bubble_shown_today=t.sent')` — the `t.sent` regenerator
  // artifact and WebKit phrasing mark it as third-party minified code. The
  // exception is synthetic with ZERO stack frames, so URL/frame-based filters
  // have nothing to match on — a message entry is the only suppression point
  // that works, exactly as with the `resp.payload` Bloomreach case above.
  // Neither `activityData` nor `bubble_shown_today` appears anywhere in the
  // repo, so this is never site code. Because the only meaningful frame is the
  // page URL, one session fans out into a fresh issue per landing page. The
  // `bubble_shown_today` token is unique (like `CoinType`), so it is safe as a
  // bare substring and cannot collide with the server-side Sentry
  // `ignoreErrors` that also consume this list.
  "bubble_shown_today",
  // A minified third-party countdown / offer-gating widget injected on the
  // storefront reads `this.pageConfig.config.disabledUntilTimestamp` in its
  // bundle and throws when `pageConfig.config` is undefined. Safari surfaces
  // this as `undefined is not an object (evaluating
  // 'this.pageConfig.config.disabledUntilTimestamp')` — the WebKit phrasing
  // marks it as browser-only, third-party minified code. The exception is
  // synthetic with ZERO stack frames, so URL/frame-based filters have nothing
  // to match on — a message entry is the only suppression point that works,
  // exactly as with the `resp.payload` Bloomreach and `bubble_shown_today`
  // activity-bubble cases above. Neither `pageConfig` nor
  // `disabledUntilTimestamp` appears anywhere in the repo, so this is never
  // site code. Because the only meaningful frame is the page URL, one session
  // fans out into a fresh issue per landing page. The `disabledUntilTimestamp`
  // token is unique (like `CoinType`), so it is safe as a bare substring and
  // cannot collide with the server-side Sentry `ignoreErrors` that also consume
  // this list.
  "disabledUntilTimestamp",
  // posthog-js's own network layer aborts its internal analytics fetch when the
  // request outlives its timeout (client network stall, ad blocker, or page
  // teardown) and reports it as `AbortError: PostHog request timed out after
  // <n>ms`. It is built by `timeoutAbortReason()` in the SDK's own
  // `request.ts`, so the only stack frame is inside the posthog-js bundle —
  // there is never a site-code frame and nothing in our code to fix; all that
  // happened is one analytics request got dropped. Matched on the message
  // substring rather than the frame path, which embeds the pinned
  // `posthog-js@<version>` and would silently stop matching on the next
  // catalog bump; the substring also covers the timeout-less phrasing the SDK
  // emits when no timeout value is set. Because the SDK forces
  // `name === 'AbortError'`, one dropped request otherwise spawns a fresh
  // Error Tracking issue per landing page.
  "PostHog request timed out",
  // An injected third-party script (a Facebook/Instagram in-app-browser
  // script or a GTM Custom HTML tag maintained outside the repo) does
  // `document.querySelector('button[aria-label*="search"]').addEventListener(...)`
  // to wire up a storefront search button. Landing pages legitimately never
  // render a search button, so `querySelector` returns null and WebKit throws
  // `null is not an object (evaluating '…addEventListener')`. The selector
  // `button[aria-label*="search"]` exists nowhere in the repo, and the only
  // stack frame is the page-document URL itself (line 1 of the HTML, not a
  // `/_next/static/` chunk) — so this is never site code. Because that frame is
  // the page URL, one session fans out into a fresh issue per landing page.
  // Matched on the repo-absent CSS-selector substring so every per-page
  // fingerprint collapses into one; the selector cannot appear in a genuine
  // server-side error, so it is safe in this shared list.
  'button[aria-label*="search"]',
]

/**
 * URL patterns to deny — errors originating from these URLs are dropped.
 * Stored as regex source strings for serialization into inline scripts.
 * Use with `new RegExp(pattern, "i")` at runtime.
 */
export const DENIED_URL_PATTERNS = [
  // Exponea endpoints
  "api\\.exponea\\.com",
  "exp\\.baerskintactical\\.com",
  "exp\\.baerskinhoodie\\.com",
  "exp\\.divcdn\\.com",
  "exp\\..*\\.com",
  "exponea(?:\\.min)?\\.js",
  "/webxp/",
  // Payments
  "www\\.paypal\\.com",
  // Yuma chat widget — the third-party bundle at js.yuma.ai/widget.js throws
  // `TypeError: null is not a constructor` from its minified loadEncryptor in
  // certain client environments (missing Web Crypto, embedded/old browsers,
  // partial loads behind script blockers). The failing logic lives entirely in
  // Yuma's hosted bundle; the only repo touchpoint is the injector in
  // scripts/yuma.tsx, so there is no in-repo logic fix. Drop these so the
  // widget's internal errors don't surface as app errors / inbox noise.
  "js\\.yuma\\.ai",
  // Facebook flakiness
  "graph\\.facebook\\.com",
  "connect\\.facebook\\.net/en_US/all\\.js",
  // Chrome extensions
  "extensions/",
  "^chrome://",
  // Google
  "googletagmanager\\.com",
  "/gtm\\.js",
  "pagead2\\.googlesyndication\\.com",
  "pagead/js",
  "analytics\\.google\\.com",
  // Woopra
  "eatdifferent\\.com\\.woopra-ns\\.com",
  "static\\.woopra\\.com/js/woopra\\.js",
  // ID5 identity service (adtech) — diagnostics.id5-sync.com throws fetch
  // NetworkErrors from its own script, not site code.
  "id5-sync\\.com",
  // Microsoft Clarity (loaded via GTM) — its recorder throws "Maximum call
  // stack size exceeded" from clarity.js on deeply nested DOMs; internal to
  // Microsoft's bundle, not site code.
  "clarity\\.ms",
  "/clarity\\.js",
  // Third-party pixel bundle served at /p/<id>/b/bs.<hash>-N.iife.js —
  // throws "Right-hand side of 'instanceof' is not an object" from its own
  // minified code; no such bundle is built from this repo.
  "/p/\\d+/b/bs\\.",
  // Obfuscated third-party tracking pixel (throws navigator[r(478)] /
  // window[...] is-not-a-function and sendBeacon errors). Generic filename —
  // matched by path because the serving host varies; no pixel.js exists in
  // any apps/*/public dir or rewrite.
  "/pixel\\.js",
  // Twitter/X universal website tag — throws URIError from its own bundle.
  "/uwt\\.js",
  // A third-party loader script (browser extension / injected tag) served at
  // /loader.js throws `QuotaExceededError: The quota has been exceeded.` from
  // its own getCookie/gfpGetCookie while writing cookies past the browser
  // storage quota. No loader.js is built from this repo (nothing under
  // apps/*/public or any rewrite) and our own storage writes in
  // packages/utils/src/storageAvailable.ts already catch this — so it is never
  // site code. The synthetic DOMException has no /_next/static/ frame, only
  // /loader.js line 1, so this class of loader noise fans one session out into
  // a fresh issue per landing page. Matched by path (our bundles live under
  // /_next/static/) mirroring the /pixel.js and /uwt.js precedents above.
  "/loader\\.js",
  // ID5 bootstrap served under a first-party-looking path — the
  // id5-sync.com entry above misses it (URI malformed + webview bridge
  // errors from the same script).
  "/id5-bootstrap\\.js",
  // PostHog session-recorder bundle (rrweb-record.js) — its instrumentation
  // wraps page listeners and throws e.removeEventListener-is-not-a-function
  // when a third-party script replaced addEventListener. Recorder-internal,
  // shopping flow unaffected (STORE-4JT).
  "rrweb-record",
  // posthog-js error-tracking lazy chunk (errorTracking-<hash>.js) — its own
  // wrapper crashes on non-Error throwables (r.message.toLowerCase). PostHog
  // SDK internals, not site code (STORE-393 / STORE-2PV / STORE-2Q1).
  "errorTracking-",
  // Other plugins
  "127\\.0\\.0\\.1:4001/isrunning",
  "webappstoolbarba\\.texthelp\\.com/",
  "metrics\\.itunes\\.apple\\.com\\.edgesuite\\.net/",
]

/**
 * Whether an exception should be suppressed based on its message text.
 *
 * Several `IGNORED_ERROR_MESSAGES` entries carry an error-type prefix
 * (e.g. `"TypeError: NetworkError when attempting to fetch resource"`). The
 * top-level `$exception_message` includes that prefix, but a structured
 * `$exception_list[].value` holds only the bare message — the type lives
 * separately in `$exception_list[].type`. Matching the prefixed entry against
 * the bare value would never succeed (the entry is longer than the value), so
 * when a `type` is supplied we also test the reconstructed `"type: value"`
 * form. Pass `type` only for `$exception_list` entries; omit it for the
 * already-prefixed `$exception_message`.
 */
export const isIgnoredExceptionMessage = (
  value: string | null | undefined,
  type?: string | null
): boolean => {
  if (!value) {
    return false
  }

  const candidates = type ? [value, `${type}: ${value}`] : [value]

  return candidates.some((candidate) =>
    IGNORED_ERROR_MESSAGES.some((ignored) => candidate.indexOf(ignored) !== -1)
  )
}

/** GTM-specific patterns for stack trace inspection */
export const GTM_PATTERNS = [
  "googletagmanager\\.com",
  "/gtm\\.js",
  "/securemetric",
  "/baerskintactical\\.js",
]

export const BLOOMREACH_UNDEFINED_VALUE_ERROR =
  "Cannot read properties of undefined (reading 'value')"

export const BLOOMREACH_TRACE_PATTERNS = [
  "api\\.exponea\\.com",
  "exp\\.baerskintactical\\.com",
  "exponea(?:\\.min)?\\.js",
  "/webxp/",
  "webxpClient",
  "modifications\\.min\\.js",
]

export const BLOOMREACH_BREADCRUMB_PATTERNS = [
  "step-one-form",
  "step-two-form",
  "step-three-form",
  "form-Teaser",
  "form-Email",
  "form-Phone",
  "iti--allow-dropdown",
]

interface BreadcrumbCandidate {
  message?: string | null
  data?: unknown
}

interface BloomreachValueErrorCandidate {
  breadcrumbs?: BreadcrumbCandidate[]
  frames?: Array<string | null | undefined>
  message?: string | null
  rawStack?: string | null
}

const matchesAnyPattern = (
  values: Array<string | null | undefined>,
  patterns: string[]
): boolean => {
  return values.some((value) => {
    if (!value) {
      return false
    }

    return patterns.some((pattern) => new RegExp(pattern, "i").test(value))
  })
}

const stringifyBreadcrumb = (breadcrumb: BreadcrumbCandidate): string[] => {
  const values: string[] = []

  if (breadcrumb.message) {
    values.push(breadcrumb.message)
  }

  if (typeof breadcrumb.data === "string") {
    values.push(breadcrumb.data)
    return values
  }

  if (!breadcrumb.data) {
    return values
  }

  try {
    values.push(JSON.stringify(breadcrumb.data))
  } catch {
    return values
  }

  return values
}

export const isBloomreachUndefinedValueError = ({
  breadcrumbs = [],
  frames = [],
  message,
  rawStack,
}: BloomreachValueErrorCandidate): boolean => {
  if (message !== BLOOMREACH_UNDEFINED_VALUE_ERROR) {
    return false
  }

  if (matchesAnyPattern([...frames, rawStack], BLOOMREACH_TRACE_PATTERNS)) {
    return true
  }

  const breadcrumbValues = breadcrumbs.flatMap(stringifyBreadcrumb)

  return matchesAnyPattern(breadcrumbValues, BLOOMREACH_BREADCRUMB_PATTERNS)
}

/**
 * Frame pattern for the rrweb session recorder bundle shipped by posthog-js
 * (`posthog-recorder.js`).
 */
export const POSTHOG_RECORDER_FRAME_PATTERN = "posthog-recorder"

interface PosthogRecorderSecurityErrorCandidate {
  /** Error name / PostHog `$exception_list[].type` (e.g. "SecurityError"). */
  type?: string | null
  /** Exception message / PostHog `$exception_list[].value`. */
  message?: string | null
  /** Stack-frame filenames to test against the recorder bundle. */
  frames?: Array<string | null | undefined>
}

/**
 * The PostHog session recorder walks the DOM and trips over cross-origin
 * iframes (Stripe, PayPal, YouTube embeds): touching `parentNode` across the
 * boundary makes the browser throw a `SecurityError` DOMException (Firefox:
 * "Permission to call 'get parentNode' denied"). This is recorder noise — not
 * site code — and session replay keeps working everywhere outside the blocked
 * iframe, so it's dropped from PostHog error tracking.
 */
export const isPosthogRecorderSecurityError = ({
  type,
  message,
  frames = [],
}: PosthogRecorderSecurityErrorCandidate): boolean => {
  const isSecurityError =
    type === "SecurityError" ||
    type === "DOMException" ||
    (typeof message === "string" && /SecurityError/i.test(message))

  if (!isSecurityError) {
    return false
  }

  return matchesAnyPattern(frames, [POSTHOG_RECORDER_FRAME_PATTERN])
}

/**
 * Frame/stack pattern for Next.js's RSC (React Server Components) Flight client
 * bundle — `react-server-dom-<bundler>-client.browser.production.js`. The
 * turbopack chunk path embeds the pnpm lockfile hash, which shifts on every
 * toolchain bump, but the module name is stable, so match on that.
 */
export const RSC_FLIGHT_CLIENT_FRAME_PATTERN = "react-server-dom-"

interface RscConnectionClosedCandidate {
  /** Exception message / PostHog `$exception_list[].value` or `$exception_message`. */
  message?: string | null
  /** Stack-frame filenames to test against the Flight client bundle. */
  frames?: Array<string | null | undefined>
  /** Raw stack trace string, used when structured frames are unavailable. */
  rawStack?: string | null
}

/**
 * Next.js's RSC/Flight client throws `Connection closed.` when a streaming
 * Server Component response is closed before it finishes — the visitor
 * navigated away, closed the tab, or dropped network mid-navigation. It is
 * transient browser/transport behavior in Next.js internals, not an app bug,
 * and never recurs per session. The turbopack chunk frame embeds the pnpm
 * lockfile hash, so the stack fingerprint shifts on every toolchain bump and
 * spawns a fresh Error Tracking issue for the same abort.
 *
 * `Connection closed.` is deliberately NOT added to `IGNORED_ERROR_MESSAGES`:
 * that list can also be consumed by server-side error handling (Node/edge
 * runtimes in the `store` instrumentation), where a bare
 * substring would silently drop genuine upstream/database connection-drop
 * exceptions that have nothing to do with the browser RSC abort. Scoping to the
 * Flight client stack frame — which only exists in the browser bundle — keeps
 * the suppression tied to the actual noise source.
 */
export const isRscConnectionClosedError = ({
  message,
  frames = [],
  rawStack,
}: RscConnectionClosedCandidate): boolean => {
  if (!message || message.indexOf("Connection closed.") === -1) {
    return false
  }

  return matchesAnyPattern(
    [...frames, rawStack],
    [RSC_FLIGHT_CLIENT_FRAME_PATTERN]
  )
}
